import { Button } from "@design-system/components/Button";
import { trans } from "@mongez/localization";
import { useEffect } from "react";
import { Portal } from "../../design-system/components/Portals";
import { paymentAtom, paymentPortal } from "../atoms";
import { useCurrentPaymentMethod, usePaymentMethodConfig } from "../hooks";
import { CodePayment } from "./CodePayment";
import { FawryPayment } from "./FawryPayment";
import { PaymentDetails } from "./PaymentDetails";
import { PaymentMethodsGrid } from "./PaymentMethodsGrid";
import { PaymentSubmitButton } from "./PaymentSubmitButton";
import { PaymentSummary } from "./PaymentSummary";
import { ReceiptUpload } from "./ReceiptUpload";

export function PaymentPortal() {
  const paymentMethodConfig = usePaymentMethodConfig();

  const { paymentMethod } = useCurrentPaymentMethod();

  const opened = paymentPortal.use("opened");

  useEffect(() => {
    if (!opened) {
      paymentAtom.silentReset();
    }
  }, [opened]);

  const renderPaymentContent = () => {
    if (!paymentMethod) return null;

    // Handle online payments (Fawry and Card)
    if (["fawry", "card"].includes(paymentMethod)) {
      return <FawryPayment />;
    }

    if (paymentMethod === "code") {
      return <CodePayment />;
    }

    // Handle manual payments (vodafoneCash, instaPay, bankTransfer, code)
    return (
      <>
        <PaymentDetails />

        {paymentMethodConfig[paymentMethod]?.requiresReceipt && (
          <ReceiptUpload />
        )}

        <PaymentSummary />
      </>
    );
  };

  const showManualPaymentActions =
    paymentMethod && !["fawry", "card"].includes(paymentMethod);

  return (
    <Portal portal={paymentPortal} width="md" closeButton overlay clickOutside>
      <div className="max-h-[80vh] space-y-6 overflow-y-auto overflow-x-hidden">
        <div className="text-center space-y-4">
          <div className="relative">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-primary to-gray-900 dark:from-gray-100 dark:via-primary-light dark:to-gray-100 bg-clip-text text-transparent">
              {trans("invoices.choosePaymentMethod")}
            </h2>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-primary to-primary/50 rounded-full"></div>
          </div>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-lg">
            {trans("invoices.selectPaymentMethodDescription")}
          </p>
        </div>

        <div className="relative">
          <PaymentMethodsGrid />
        </div>

        {renderPaymentContent()}

        {showManualPaymentActions && (
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={paymentPortal.close}
              className="flex-1"
            >
              {trans("invoices.cancel")}
            </Button>
            <PaymentSubmitButton />
          </div>
        )}
      </div>
    </Portal>
  );
}
